export interface HierarchyTreeOption {
  value: string
  label: string
  parentValue?: string
  deleted?: boolean
}

export type ParentKey = string | null

export const ROOT_PARENT: ParentKey = null
export const MAX_HIERARCHY_DEPTH = 4
const ANCESTOR_PATH_SEPARATOR = '/'

export const buildOptionsByValue = <T extends HierarchyTreeOption>(
  options: T[],
): Map<string, T> => new Map(options.map(option => [option.value, option]))

export const resolveParentKey = <T extends HierarchyTreeOption>(
  option: T,
  optionsByValue: Map<string, T>,
): ParentKey =>
  option.parentValue && optionsByValue.has(option.parentValue)
    ? option.parentValue
    : ROOT_PARENT

export const buildChildrenByParent = <T extends HierarchyTreeOption>(
  options: T[],
  optionsByValue: Map<string, T>,
): Map<ParentKey, T[]> => {
  const children = new Map<ParentKey, T[]>()
  options.forEach(option => {
    const parentKey = resolveParentKey(option, optionsByValue)
    const list = children.get(parentKey) || []
    list.push(option)
    children.set(parentKey, list)
  })
  return children
}

export const hasChildren = <T extends HierarchyTreeOption>(
  value: string,
  childrenByParent: Map<ParentKey, T[]>,
): boolean => !!childrenByParent.get(value)?.length

// Root-first chain of the option and its ancestors. Cycle-guarded; stops at a
// missing/orphaned parent (that node is treated as a root).
export const buildAncestorChain = <T extends HierarchyTreeOption>(
  value: string,
  optionsByValue: Map<string, T>,
): T[] => {
  const node = optionsByValue.get(value)
  if (!node) return []

  const chain: T[] = []
  const visited = new Set<string>()
  let current: T | undefined = node

  while (current && !visited.has(current.value)) {
    visited.add(current.value)
    chain.push(current)
    const parentKey = resolveParentKey(current, optionsByValue)
    current = parentKey ? optionsByValue.get(parentKey) : undefined
  }

  return chain.reverse()
}

// Ancestors of a value (root -> parent), excluding the value itself.
export const buildAncestorPathLabel = <T extends HierarchyTreeOption>(
  value: string,
  optionsByValue: Map<string, T>,
): string =>
  buildAncestorChain(value, optionsByValue)
    .slice(0, -1)
    .map(option => option.label)
    .join(ANCESTOR_PATH_SEPARATOR)

// Leaf-first display label: "<Leaf label> <ancestor/path>" (path omitted for
// root-level options). Returns '' when the value is not found.
export const buildHierarchyDisplayLabel = <T extends HierarchyTreeOption>(
  value: string,
  optionsByValue: Map<string, T>,
): string => {
  const chain = buildAncestorChain(value, optionsByValue)
  if (!chain.length) return ''

  const leaf = chain[chain.length - 1]
  const ancestorPath = chain
    .slice(0, -1)
    .map(option => option.label)
    .join(ANCESTOR_PATH_SEPARATOR)

  return ancestorPath ? `${leaf.label} ${ancestorPath}` : leaf.label
}

// Leaf-first display label, degrading gracefully: full path → bare label → raw
// value (so an option missing from the set still renders something stable).
export const resolveHierarchyLabel = <T extends HierarchyTreeOption>(
  value: string,
  optionsByValue: Map<string, T>,
): string =>
  buildHierarchyDisplayLabel(value, optionsByValue) ||
  optionsByValue.get(value)?.label ||
  value

export interface HierarchyDisplayParts {
  leaf: string
  ancestorPath: string
}

// The same leaf-first display split as resolveHierarchyLabel, but returned as
// separate parts so each surface can style the ancestor path on its own (dimmed)
// without re-deriving the tree walk.
export const buildHierarchyDisplayParts = <T extends HierarchyTreeOption>(
  value: string,
  optionsByValue: Map<string, T>,
): HierarchyDisplayParts => {
  const chain = buildAncestorChain(value, optionsByValue)
  if (!chain.length) {
    return { leaf: optionsByValue.get(value)?.label || value, ancestorPath: '' }
  }

  return {
    leaf: chain[chain.length - 1].label,
    ancestorPath: chain
      .slice(0, -1)
      .map(option => option.label)
      .join(ANCESTOR_PATH_SEPARATOR),
  }
}

// Values that are a parent of at least one non-deleted option.
export const getParentValues = <T extends HierarchyTreeOption>(
  options: T[],
): Set<string> => {
  const parents = new Set<string>()
  options.forEach(option => {
    if (!option.deleted && option.parentValue) {
      parents.add(option.parentValue)
    }
  })
  return parents
}

export const getDescendantValues = <T extends HierarchyTreeOption>(
  value: string,
  options: T[],
): Set<string> => {
  const descendants = new Set<string>()
  const visited = new Set<string>([value])

  const visit = (parentValue: string) => {
    options.forEach(option => {
      if (
        !option.deleted &&
        option.parentValue === parentValue &&
        !visited.has(option.value)
      ) {
        visited.add(option.value)
        descendants.add(option.value)
        visit(option.value)
      }
    })
  }

  visit(value)
  return descendants
}

export const getSubtreeMaxRelativeDepth = <T extends HierarchyTreeOption>(
  value: string,
  options: T[],
): number => {
  const visited = new Set<string>([value])

  const visit = (parentValue: string, depth: number): number =>
    options.reduce((maxDepth, option) => {
      if (
        option.deleted ||
        option.parentValue !== parentValue ||
        visited.has(option.value)
      ) {
        return maxDepth
      }

      visited.add(option.value)
      return Math.max(maxDepth, depth, visit(option.value, depth + 1))
    }, depth)

  return visit(value, 0)
}

// Options to show given soft-deletes: drop any option whose own chain (itself or
// an ancestor) is archived, EXCEPT the preserved value's full path — so a record
// still pointing at an archived option keeps rendering its label until changed.
export const getVisibleOptions = <T extends HierarchyTreeOption>(
  options: T[],
  preservedValue?: string | null,
): T[] => {
  const optionsByValue = buildOptionsByValue(options)
  const preservedPath = new Set(
    preservedValue
      ? buildAncestorChain(preservedValue, optionsByValue).map(
          option => option.value,
        )
      : [],
  )

  return options.filter(
    option =>
      preservedPath.has(option.value) ||
      buildAncestorChain(option.value, optionsByValue).every(
        node => !node.deleted,
      ),
  )
}

// Depth of an option in the live tree (root options are depth 0). Orphaned and
// unknown values resolve to 0, matching how they render as roots.
export const getOptionDepth = <T extends HierarchyTreeOption>(
  value: string,
  options: T[],
): number => {
  const liveByValue = buildOptionsByValue(
    options.filter(option => !option.deleted),
  )
  return Math.max(0, buildAncestorChain(value, liveByValue).length - 1)
}

// Whether a subtree (rooted at subtreeRootValue) fits under targetParentValue
// (undefined = root) without exceeding MAX_HIERARCHY_DEPTH. The single home for
// the depth-limit rule shared by indent, drag-reorder, and revive.
export const canPlaceSubtree = <T extends HierarchyTreeOption>(
  options: T[],
  subtreeRootValue: string,
  targetParentValue: string | undefined,
): boolean => {
  const targetDepth = targetParentValue
    ? getOptionDepth(targetParentValue, options) + 1
    : 0
  return (
    targetDepth + getSubtreeMaxRelativeDepth(subtreeRootValue, options) <=
    MAX_HIERARCHY_DEPTH
  )
}

// Parses a property's `configuration.options` (array of objects/strings or a
// JSON string) into the canonical option shape. Visibility/`isNew` policies are
// layered on by callers.
export const parseHierarchyOptions = (
  rawOptions: unknown,
): HierarchyTreeOption[] => {
  if (!rawOptions) return []

  const result: HierarchyTreeOption[] = []
  try {
    const configOptions = Array.isArray(rawOptions)
      ? rawOptions
      : JSON.parse(rawOptions as string)

    for (const option of configOptions) {
      if (typeof option === 'string') {
        result.push({ value: option, label: option })
      } else if (option?.value && option?.label) {
        result.push({
          value: option.value,
          label: option.label,
          parentValue: option.parentValue || undefined,
          deleted: option.deleted || undefined,
        })
      }
    }
  } catch (error) {
    console.warn('Failed to parse hierarchy options:', error)
  }

  return result
}
